// Copyright (c) Move Closer 2022.

import Component from 'vue-class-component'
import { Vue } from 'vue-property-decorator'

import { AllowedAttributes, CartItemData, ProductData } from '../../../contexts'

import { CartModel } from '../../checkout/contracts'

import { CandidateGift } from '../contracts/gifts'

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
@Component({
  name: 'GiftAbstract'
})
export class GiftAbstract extends Vue {
  public canPickGifts: boolean = true
  public canPickSamples: boolean = true

  /**
   * Checks if current user can pick gifts/samples.
   * (samples can not be picked any more if at least one cart item has truthy 'isSample' property)
   * @private
   */
  protected checkIfUserCanPick (cart: CartModel | null, attribute: string): void {
    if (!cart) {
      return
    }

    const tempItems = []
    for (const item of cart.items) {
      for (const variant of Object.values(item.product.variants)) {
        if (variant.attributes[attribute]) {
          tempItems.push(variant)
        }
      }
    }

    if (attribute === AllowedAttributes.IsSample) {
      this.canPickSamples = tempItems.length === 0
    } else if (attribute === AllowedAttributes.IsGift) {
      this.canPickGifts = tempItems.length === 0
    }
  }

  /**
   * Returns gifts that are available
   * @param products
   * @private
   */
  protected getAvailableProductsOnly (products: Array<ProductData>): Array<ProductData> {
    return products.filter((product) => {
      return Object.values(product.variants)[0].isAvailable || Object.values(product.variants)[0].sellableQuantity > 0
    })
  }

  /**
   * Retrieves gifts from all cartItems with proper quantity that should be added to cart.
   * @param cartItems
   * @param uid (optional) - uid of product that has giftsSkus (used when updating cartItem quantity).
   * @param hasBeenRestored - indicates whether gifts should be restored with a restored product.
   * @protected
   */
  protected retrieveGiftsFromProduct (cartItems: Array<CartItemData>, uid: string | null, hasBeenRestored: boolean): Array<CandidateGift> {
    if (uid) {
      const selected = cartItems.find(item => item.uid === uid)
      if (selected) {
        return this.translateToSkus([selected], AllowedAttributes.GiftsSku, hasBeenRestored)
      }
    }

    return this.translateToSkus(cartItems, AllowedAttributes.GiftsSku)
  }

  /**
   * Returns array of gift/samples skus assigned to cart items
   * @private
   */
  protected translateToSkus (items: Array<CartItemData>, attribute: string, hasBeenRestored: boolean = false): Array<CandidateGift> {
    const temp: Array<CandidateGift> = []

    for (const item of items) {
      for (const variant of Object.values(item.product.variants)) {
        /** Use only available gifts */
        if (variant.isAvailable) {
          if (variant.attributes[attribute] && (variant.attributes[attribute] as Array<string>).length > 0) {
            for (const sku of Object.values(variant.attributes[attribute])) {
              temp.push({
                sku,
                quantity: hasBeenRestored ? 1 : item.quantity
              })
            }
          }
        }
      }
    }

    return temp
  }
}
